import useSWR, { BareFetcher, useSWRConfig } from 'swr';
import { walletService } from '@/services/api';
import { config } from '@/config';
import { FetchErrorsType, AwaitedReturnType } from '@/types';
import { createTransactionHistoryQueryKey } from '@/utils/wallet/createTransactionHistoryQueryKey';
import { useRouter } from '@/hooks/general/useRouter/useRouter';

const API_URL = config.api.url.wallet;

export const useAccountsQuery = () => {
    const router = useRouter();
    const { data, error } = useSWR<
        AwaitedReturnType<typeof walletService.getAccounts>,
        FetchErrorsType
    >(`${API_URL}`, () => walletService.getAccounts({ routerPush: router.push }));

    return {
        accounts: data,
        isLoading: !data && !error,
        error,
    };
};

export const useTransactionsQuery = (
    accountId: number | null | undefined,
    showArchivedTransactions: boolean,
) => {
    const router = useRouter();
    const fetcher: BareFetcher<AwaitedReturnType<typeof walletService.getTransactions>> = ([
        ,
        accountId,
        showArchivedTransactions,
    ]: [_: string, accountId: number, showArchivedTransactions: boolean]) =>
        walletService.getTransactions(accountId, showArchivedTransactions, {
            routerPush: router.push,
        });

    const { data, error } = useSWR<
        AwaitedReturnType<typeof walletService.getTransactions>,
        FetchErrorsType
    >(accountId ? [`${API_URL}/transactions`, accountId, showArchivedTransactions] : null, fetcher);

    return {
        transactionsWithCount: data,
        isLoading: !data && !error,
        error,
    };
};

export const useTransactionHistoryQuery = (
    transactionId: number | null,
    languageId: number | null,
    token?: string,
) => {
    const key = createTransactionHistoryQueryKey(transactionId, languageId, token);
    const router = useRouter();
    const fetcher: BareFetcher<AwaitedReturnType<typeof walletService.getTransactionHistory>> = ([
        ,
        transactionId,
        languageId,
        token,
    ]: [_: string, transactionId: number, languageId: number, token: string | undefined]) =>
        walletService.getTransactionHistory(transactionId, languageId, token, {
            routerPush: router.push,
        });

    const { data, error } = useSWR<
        AwaitedReturnType<typeof walletService.getTransactionHistory>,
        FetchErrorsType
    >(key, fetcher);

    return {
        transactionHistory: data,
        isLoading: !data && !error,
        error,
    };
};

export const useParticipationInfoQuery = (key: string | undefined) => {
    const router = useRouter();
    const fetcher: BareFetcher<AwaitedReturnType<typeof walletService.getParticipationInfo>> = ([
        ,
        key,
    ]: [_: string, key: string]) =>
        walletService.getParticipationInfo(key, { routerPush: router.push });

    const { data, error } = useSWR<
        AwaitedReturnType<typeof walletService.getParticipationInfo>,
        FetchErrorsType
    >(key ? [`${API_URL}/participation`, key] : null, fetcher);

    return {
        participationInfo: data,
        isLoading: !data && !error,
        error,
    };
};

export const usePaymentRequestStartData = (accountId?: number) => {
    const router = useRouter();
    const fetcher: BareFetcher<
        AwaitedReturnType<typeof walletService.getPaymentRequestStartData>
    > = ([, accountId]: [_: string, accountId: number]) =>
        walletService.getPaymentRequestStartData(accountId, { routerPush: router.push });

    const { data, error } = useSWR<
        AwaitedReturnType<typeof walletService.getPaymentRequestStartData>,
        FetchErrorsType
    >(accountId ? [`${API_URL}`, accountId] : null, fetcher);

    return {
        paymentRequestStartData: data,
        isLoading: !data && !error,
        error,
    };
};

export const useAmazonGiftCardQuery = (transactionId: number) => {
    const router = useRouter();
    const { data, error } = useSWR<
        AwaitedReturnType<typeof walletService.amazonGiftCard>,
        FetchErrorsType
    >(
        [`${API_URL}/transaction/amazon-gift-card`, transactionId],
        () => walletService.amazonGiftCard(transactionId, { routerPush: router.push }),
        {
            errorRetryCount: 1,
            errorRetryInterval: 30000,
        },
    );
    return {
        amazonGiftCard: data,
        isLoading: !data && !error,
        error,
    };
};

export const useNonMemberDataQuery = (
    token: string | undefined,
    paymentMethodId?: number | undefined,
) => {
    const key = [`${API_URL}/non-member-payment`, token, paymentMethodId];
    const { mutate } = useSWRConfig();
    const fetcher: BareFetcher<AwaitedReturnType<typeof walletService.getNonMemberData>> = ([
        ,
        token,
        paymentMethodId,
    ]: [_: string, token: string, paymentMethodId?: number]) =>
        walletService.getNonMemberData(token, paymentMethodId);

    const { data, error, isLoading } = useSWR<
        AwaitedReturnType<typeof walletService.getNonMemberData>,
        FetchErrorsType
    >(token ? key : null, fetcher);

    return { data, error, mutate: () => mutate(key), isLoading };
};

export const useNonMemberPersonalDetailsQuery = (token: string | undefined) => {
    const router = useRouter();
    const { mutate } = useSWRConfig();
    const SWR_KEY = token
        ? [`${API_URL}/non-member-payment/${token ?? ''}/personal-details`, token]
        : null;

    const { data, error, isLoading } = useSWR<
        AwaitedReturnType<typeof walletService.getNonMemberPersonalData>,
        FetchErrorsType
    >(
        SWR_KEY,
        ([, token]: [_: string, token: string]) =>
            walletService.getNonMemberPersonalData(token, { routerPush: router.push }),
        { shouldRetryOnError: false },
    );

    return { data, error, mutate: () => mutate(SWR_KEY), isLoading };
};
